<template>
  <div :id="computedId">
    <GenericFormModal
      :action="serverId == null ? this.$api.addServer : this.$api.editServer"
      :canSubmit="canSubmitData"
      :title="title"
      :isShown="isShown"
      :actionId="serverId == null ? undefined : serverId"
      :otherValues="computedOtherValues"
      :showButtons="isReadyToShow"
      :id="computedId + '-modal'"
      @canceled="cancelOperation"
      @submited="onSubmitedData"
    >
      <div v-if="isReadyToShow" class="w-100 h-100">
        <InputTextName
          :initialValue="initialName"
          :identifier="'nom'"
          :label="'sections.formServerSection.serverName' | i18n"
          :icon="'user circle'"
          :forcedValidations="serverNameForcedValidations"
          @onChange="
            data => {
              isServerNameValid = data.validation.isValid;
            }
          "
        />
        <InputTextIpAddress
          :initialValue="initialIp"
          :identifier="'ip'"
          :label="'sections.formServerSection.ipAddress' | i18n"
          :forcedValidations="ipAddressForcedValidations"
          @onChange="
            data => {
              isIpAddressValid = data.validation.isValid;
            }
          "
        />
      </div>
      <div
        v-else
        class="d-flex justify-content-center align-items-center h-100"
      >
        <b-spinner class="m-auto text-light" />
      </div>
    </GenericFormModal>
    <b-toast
      :id="computedToastId"
      toast-class="bg-danger text-white text-center"
      :no-close-button="true"
    >
      <template v-slot:default>
        <i class="exclamation circle icon mr-3"></i>
        <span>{{ "sections.formServerSection.error" | i18n }}</span>
      </template>
    </b-toast>
  </div>
</template>
<script>
import GenericFormModal from "@/components/generics/GenericFormModal.vue";
import InputTextName from "@/components/inputs/InputTextName.vue";
import InputTextIpAddress from "@/components/inputs/InputTextIpAddress.vue";
/**
 * @description Formulaire pour ajouter un nouveau serveur au client connecté
 * @author Samuel Colassin
 * @author Alexandre Gallant
 * @vue-prop {Boolean} isShown - Si le formulaire doit être affiché
 * @vue-prop {Number} serverId - Id du serveur à modifier
 * @vue-prop {String} clientId - L'id du client lié au serveur
 * @vue-data {Boolean} isServerNameValid - Si le nom du serveur est valide dans le formulaire
 * @vue-data {Boolean} isIpAddressValid - Si l'adresse ip du serveur est valide dans le formulaire
 * @vue-data {Number} serverNameForcedValidations - Nombre de fois que les validations ont été forcées pour «city»
 * @vue-data {Number} ipAddressForcedValidations - Nombre de fois que les validations ont été forcées pour «url»
 * @vue-data {Boolean} isReadyToShow - Si le formulaire est prêt à être affiché
 * @vue-data {String} initialName - La valeur initiale du champ nom
 * @vue-data {String} initialIp - La valeur initiale du champ ip
 * @vue-data {String} serverClientId - L'id du client lié au serveur (utilisé lors de la modification de serveur)
 * @vue-data {String} title - Le titre du formulaire
 * @vue-computed {Object} computedOtherValues - Les valeurs à envoyer dans la requête et qui ne sont pas dans le formulaire
 * @vue-computed {String} computedClientId - Id à utiliser pour le client
 * @vue-computed {String} computedId - L'id à donner au modal
 * @vue-computed {String} computedToastId - L'id du toast
 * @vue-event {void} canceled - Est émis lorsque le formulaire est annulé
 * @vue-event {void} successed-request - Est émis lorsque le résultat de la requête est positif (200)
 */
export default {
  components: { GenericFormModal, InputTextName, InputTextIpAddress },
  name: "FormServerSection",
  props: {
    isShown: {
      type: Boolean,
      required: true
    },
    serverId: {
      type: Number,
      default: null
    },
    clientId: {
      type: String,
      required: true
    }
  },
  watch: {
    /**
     * @description Effectue les requêtes nécéssaires selon le cas
     * @param {Boolean} newValue La nouvelle valeur de «isShown»
     */
    isShown: async function(newValue) {
      if (!newValue) {
        if (this.serverId != null) {
          this.isReadyToShow = false;
        }
        return;
      }
      if (this.serverId == null) {
        return;
      }
      this.$api
        .getServer(this.serverId)
        .then(res => {
          this.initialName = res.data.data.nom;
          this.isServerNameValid = true;
          this.initialIp = res.data.data.ip;
          this.isIpAddressValid = true;
          this.serverClientId = res.data.data.client_id;
          this.isReadyToShow = true;
        })
        .catch();
    }
  },
  data() {
    return {
      isServerNameValid: false,
      isIpAddressValid: false,

      serverNameForcedValidations: 0,
      ipAddressForcedValidations: 0,

      isReadyToShow: this.serverId == null ? true : false,
      initialName: "",
      initialIp: "",
      serverClientId: null,
      title:
        this.serverId == null
          ? this.$i18n.t("sections.formServerSection.titles.add")
          : this.$i18n.t("sections.formServerSection.titles.edit")
    };
  },
  computed: {
    computedOtherValues() {
      return { client_id: this.computedClientId };
    },
    computedClientId() {
      return this.serverClientId == null ? this.clientId : this.serverClientId;
    },
    computedId() {
      return "serveur-" + (this.serverId ? this.serverId : "new");
    },
    computedToastId() {
      return "error-toast-server-" + (this.serverId ? this.serverId : "new");
    }
  },
  methods: {
    /**
     * @description Indique si l'information à envoyer est valide
     * @returns {Boolean} Vrai si les deux champs sont valides
     */
    canSubmitData() {
      const canSubmit = this.isServerNameValid && this.isIpAddressValid;
      if (!canSubmit) {
        ++this.serverNameForcedValidations;
        ++this.ipAddressForcedValidations;
      }

      return canSubmit;
    },
    /**
     * @description Émet que le formulaire a été annulé
     */
    cancelOperation() {
      this.$emit("canceled");
    },
    /**
     * @description Gère les opérations lorsque le formulaire a été envoyé
     * @param {Object} res Le résultat du submit
     */
    onSubmitedData(res) {
      res.status == 200
        ? this.$emit("successed-request")
        : this.$bvToast.show(this.computedToastId);
    }
  }
};
</script>
